import createNoAuthSupabaseClient, { getUser } from "../supabase/supabase";
import { fetchWithCache, generateCacheKey } from "@punchup/utils/caches";

import { CacheEntities } from "@punchup/utils/caches/cache-types";
import { Comedian } from "./comedian";
import { Database } from "../supabase.types";
import { SupabaseClient } from "@supabase/supabase-js";
import dayjs from "@punchup/utils/dayjs";
import { logException } from "@punchup/sentry";
import { returnNotFoundAndLogWarning } from "@punchup/utils/vercel";
import { validateSpecial } from "./specials-utils";
import { z } from "zod";

export type SpecialRow = Database["public"]["Tables"]["specials"]["Row"];
export type SpecialInsert = Database["public"]["Tables"]["specials"]["Insert"];
export type SpecialLite = Pick<
  SpecialRow,
  | "title"
  | "id"
  | "comedian_id"
  | "slug"
  | "poster_img_with_titles"
  | "runtime"
  | "price"
>;

/**
 * @deprecated Use the new SpecialRow type directly instead.
 *
 * This is left around because we cannot completely remove the type yet
 * while it's used in APIs that the mobile app use.
 */
export interface deprecated_Special {
  comedian_id: string;
  id: string;
  created_at: string | null;
  slug: string;
  title: string;
  runtime: number;
  price: number;
  requiresPurchase: boolean;
  description: string | null;
  published: Date | null;
  published_at_raw_string: string | null;
  videoID: string | null;
  smallPreviewImg: string | null;
  previewImg: string | null;
  previewVideoID: string | null;
  isLive: boolean;
  posterImageWithTitles: string | null;
  requiresSubscription: boolean;
  livestreamID: string | null;
  isVertical: boolean | null;
  captions_bucket_path: string | null;
}

export interface SpecialWithComedian extends SpecialRow {
  comedianSlug: string;
  comedianDisplayName: string;
}
export interface ComediansAndSpecialsForUser {
  comedian: {
    id: string;
    display_name: string | null;
    specials: SpecialRow[];
  } | null;
}

/**
 * @deprecated Use the new SpecialRow type directly instead.
 *
 * This is left around because we cannot completely remove the type yet
 * while it's used in APIs that the mobile app use.
 */
export function deprecated_processSpecialData(
  data: SpecialRow
): deprecated_Special {
  return {
    comedian_id: data.comedian_id,
    id: data.id,
    created_at: data.created_at,
    slug: data.slug,
    title: data.title,
    runtime: data.runtime,
    price: data.price,
    requiresPurchase: data.requires_purchase,
    description: data.description,
    published: data.published_at ? dayjs(data.published_at).toDate() : null,
    published_at_raw_string: data.published_at,
    videoID: data.video_id,
    smallPreviewImg: data.small_preview_img,
    previewImg: data.preview_img,
    previewVideoID: data.preview_video_id,
    isLive: data.is_live,
    posterImageWithTitles: data.poster_img_with_titles,
    requiresSubscription: data.requires_subscription,
    livestreamID: data.livestream,
    isVertical: data.is_vertical,
    captions_bucket_path: data.captions_bucket_path,
  };
}

/**
 * ### Get a special by its slug
 * If the special is not found, this renders the 404 Not Found page.
 * The function always returns a Special object, or execution stops so it's safe to assume
 * that if the function returns, the special was found. This allows you
 * to avoid null checking the return value when it's reasonably safe to assume the
 * special exists.
 */
export async function getSpecialOr404({
  specialSlug,
  comedianId,
}: {
  specialSlug: string;
  comedianId: string;
}): Promise<SpecialRow> {
  const supabase = createNoAuthSupabaseClient();
  const { status, data, error } = await supabase
    .from("specials")
    .select("*")
    .eq("slug", specialSlug)
    .eq("comedian_id", comedianId)
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error(error);
  } else if (status === 200) {
    if (data) {
      return data;
    } else {
      returnNotFoundAndLogWarning("Special " + specialSlug);
    }
  }
  throw new Error(`${specialSlug} not found in special table.`);
}

export async function getSpecialOrThrow({
  specialSlug,
  comedianId,
}: {
  specialSlug: string;
  comedianId: string;
}): Promise<SpecialRow> {
  const supabase = createNoAuthSupabaseClient();
  const { data, error } = await supabase
    .from("specials")
    .select("*")
    .eq("slug", specialSlug)
    .eq("comedian_id", comedianId)
    .limit(1)
    .single();

  if (error) {
    logException(error, {
      tags: {
        specialSlug,
        comedianId,
      },
    });
    throw error;
  }

  return data;
}

export async function getAllSpecialsLite(): Promise<SpecialLite[]> {
  const supabase = createNoAuthSupabaseClient();
  const { status, data, error } = await supabase
    .from("specials")
    .select(
      "id, title, comedian_id, slug, poster_img_with_titles, runtime, price"
    )
    .eq("is_live", true);

  if (error) {
    console.error(error);
  } else if (status === 200) {
    if (data) {
      return data;
    }
  }
  throw new Error(`Issue getting all specials (lite)`);
}

export type SpecialLiteWithComedianInfo = SpecialLite & {
  comedian: {
    slug: string;
    display_name: string | null;
  } | null;
} & { published_at: string | null };

export async function getSpecialsLiteByPublishedDate(
  published_at: string
): Promise<SpecialLiteWithComedianInfo[]> {
  const supabase = createNoAuthSupabaseClient();
  const { status, data, error } = await supabase
    .from("specials")
    .select(
      `
      id, title, comedian_id, slug, poster_img_with_titles, runtime, price, published_at,
      comedian:comedians!specials_comedian_id_fkey!inner (
        slug,
        display_name
      )
      `
    )
    .eq("is_live", true)
    .gt("published_at", published_at)
    .order("comedian_id");

  if (error) {
    console.error(error);
  } else if (status === 200) {
    if (data) {
      return data;
    }
  }
  throw new Error(`Issue getting all specials by published date`);
}

export async function getAllSpecialsSlugsAndTheirComedianSlug(): Promise<
  {
    slug: string;
    comedian: { slug: string } | null;
  }[]
> {
  const supabase = createNoAuthSupabaseClient();
  const { status, data, error } = await supabase.from("specials").select(
    `
    slug,
    comedian:comedians!specials_comedian_id_fkey!inner (
      slug
    )
    `
  );

  if (error) {
    console.error(error);
  } else if (status === 200) {
    if (data) {
      return data;
    }
  }
  throw new Error(
    `Error getting slugs of all specials and their comedian slug `
  );
}

export async function getAllSpecialsForComedian(
  comedianId: string
): Promise<SpecialRow[]> {
  const supabase = createNoAuthSupabaseClient();
  const { status, data, error } = await supabase
    .from("specials")
    .select("*")
    .eq("comedian_id", comedianId)
    .order("title", { ascending: true });

  if (error) {
    console.error(error);
  } else if (status === 200) {
    if (data) {
      return data;
    } else {
      returnNotFoundAndLogWarning("All specials for comedian " + comedianId);
    }
  }
  throw new Error(`Issue getting all specials for comedian: ${comedianId}`);
}

export async function getSpecialLiteByID(
  specialID: string
): Promise<SpecialLite> {
  const supabase = createNoAuthSupabaseClient();
  const { data, error } = await supabase
    .from("specials")
    .select(
      "id, title, comedian_id, slug, poster_img_with_titles, runtime, price"
    )
    .eq("id", specialID)
    .limit(1)
    .single();

  if (error) {
    console.error(error);
  } else if (data) {
    return data;
  }
  throw new Error(`${specialID} not found in specials table.`);
}

export async function createOrUpdateSpecial(
  supabase: SupabaseClient<Database>,
  special: SpecialInsert,
  callerIsSystem?: boolean
) {
  if (!callerIsSystem) {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.user) {
      throw new Error(
        "Supabase client must have an active session to create/update a show."
      );
    }
  }

  // Validate all specials in parallel

  const errors = await validateSpecial(special);
  if (errors) {
    return errors;
  }

  const { status, error, data } = await supabase
    .from("specials")
    .upsert(special, {
      onConflict: "id",
      ignoreDuplicates: false, // We set this false in order to update all fields for existing records
    })
    .select("id");

  if (error) {
    console.error(error);
    return { error: error };
  } else if (status === 201) {
    return { error: null, id: data[0].id };
  }

  throw new Error(`Error creating/updating special.`);
}

export async function userHasPurchasedSpecial(
  supabase: SupabaseClient<Database>,
  special: SpecialRow
): Promise<boolean> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) {
    return false;
  }

  const { status, data, error } = await supabase
    .from("user_specials")
    .select("user_id")
    .eq("user_id", user.id)
    .eq("special_id", special.id)
    .maybeSingle();

  if (error) {
    console.error(error);
  } else if (status === 200) {
    if (data) {
      return true;
    } else {
      return false;
    }
  }
  throw new Error(`Error finding special of ${special.id} for user ${user.id}`);
}

export async function getPurchasedSpecialIdsForUser(
  supabase: SupabaseClient<Database>
): Promise<SpecialRow["id"][]> {
  const user = await getUser(supabase);
  if (!user) {
    return [];
  }

  const { data, error } = await supabase
    .from("user_specials")
    .select("special_id")
    .eq("user_id", user.id);

  if (error) {
    logException(error);
    throw error;
  }
  const specialIDs = data?.map((row) => row.special_id);
  return specialIDs;
}

export type SpecialWithPurchasedRaw = SpecialRow &
  SpecialRow & {
    user_specials: { user_id: string }[] | null;
  };
export type SpecialWithPurchased = SpecialRow & { purchased: boolean };

export function processSpecialAndUserPurchasesData(
  data: SpecialWithPurchasedRaw
): SpecialWithPurchased {
  return {
    ...data,
    purchased: data.user_specials?.length ? true : false,
  };
}

export async function getAllPublishedSpecialsForComedian(
  comedianId: string
): Promise<SpecialRow[]> {
  const supabase = createNoAuthSupabaseClient();
  const now = dayjs().toISOString();

  const { status, data, error } = await supabase
    .from("specials")
    .select("*")
    .eq("comedian_id", comedianId)
    // special must be live
    .eq("is_live", true)
    // special must have been published before now
    .lte("published_at", now)
    // comedian must not be an external preview
    .throwOnError()
    .order("title", { ascending: true });

  if (error) {
    console.error(error);
  } else if (status === 200) {
    if (data) {
      return data;
    } else {
      returnNotFoundAndLogWarning("All specials for comedian " + comedianId);
    }
  }
  throw new Error(`Issue getting all specials for comedian: ${comedianId}`);
}

export async function getSpecialLite(slug: string): Promise<SpecialLite> {
  const supabase = createNoAuthSupabaseClient();
  const { status, data, error } = await supabase
    .from("specials")
    .select(
      "id, title, comedian_id, slug, poster_img_with_titles, runtime, price"
    )
    .eq("slug", slug)
    .maybeSingle();

  if (error) {
    console.error(error);
  } else if (status === 200) {
    if (data) {
      return data;
    }
  }
  throw new Error(`Issue getting a single special (lite). Slug was: ${slug}`);
}

const SpecialIdParam = z
  .string()
  .length(36, "specialId must be exactly 36 characters long")
  .regex(/^[a-z0-9-]+$/, "specialId contains invalid characters");

export async function getSpecialByIDWithCache(
  specialID: string
): Promise<SpecialRow> {
  // Validate that the videoId only contains acceptable characters (for security purposes)
  const parsed = SpecialIdParam.safeParse(specialID);
  if (!parsed.success) {
    logException(parsed.error, {
      extra: {
        videoId: specialID,
      },
    });
    throw new Error(`Invalid special ID: ${specialID}`);
  }

  const cacheKey = generateCacheKey(CacheEntities.SPECIAL, specialID);
  const cachedSpecial = await fetchWithCache(
    cacheKey,
    () => getSpecialByID(specialID),
    300 // 5 minutes
  );
  return cachedSpecial;
}

export async function getSpecialByID(specialID: string): Promise<SpecialRow> {
  const supabase = createNoAuthSupabaseClient();
  const { data, error } = await supabase
    .from("specials")
    .select("*")
    .eq("id", specialID)
    .limit(1)
    .single();

  if (error) {
    console.error(error);
  } else if (data) {
    return data;
  }
  throw new Error(`${specialID} not found in specials table.`);
}

// Get all specials for comedian and for each add a key `purchased`
// containing whether the user has purchased or not that special
export async function getSpecialsForComedian(
  supabase: SupabaseClient<Database>,
  comedian: Comedian
): Promise<SpecialWithPurchased[]> {
  const filter: {
    is_live: boolean;
    comedian_id: string;
    "user_specials.user_id"?: string;
  } = { is_live: true, comedian_id: comedian.id };
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (session?.user) {
    filter["user_specials.user_id"] = session.user.id;
  }
  const { status, data, error } = await supabase
    .from("specials")
    .select(
      `
      *,
      user_specials (
        user_id
      )
    `
    )
    .eq("is_live", true)
    .match(filter);

  if (error) {
    console.error(error);
  } else if (status === 200) {
    if (data) {
      const processedData = data.map((row) =>
        processSpecialAndUserPurchasesData(row)
      );
      return processedData;
    }
  }
  throw new Error(`Error getting specials and purchases`);
}

export async function deleteOneSpecial(
  supabase: SupabaseClient<Database>,
  special_id: string
) {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.user) {
    throw new Error(
      "Supabase client must have an active session to delete specials."
    );
  }

  const { status, error } = await supabase
    .from("specials")
    .delete()
    .eq("id", special_id);

  if (error) {
    console.error(error);
    return { error: error };
  } else if (status === 204) {
    return { error: null };
  }

  throw new Error(`Error deleting special with id ${special_id}`);
}

export async function getFollowedComediansAndTheirSpecialsForUser(
  supabase: SupabaseClient<Database>
): Promise<ComediansAndSpecialsForUser[]> {
  const filter: {
    user_id?: string;
  } = {};
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (session?.user) {
    filter["user_id"] = session.user.id;
  }
  const { status, data, error } = await supabase
    .from("user_comedian_follows")
    .select(
      `
      comedian:comedians!inner (
        id,
        display_name,
        specials:specials!specials_comedian_id_fkey (
          *
        )
      )
    `
    )
    .match(filter);

  if (error) {
    console.error(error);
  } else if (status === 200) {
    if (data) {
      return data;
    }
  }
  throw new Error(
    `Error getting followed comedians and their specials for a user`
  );
}

export async function getFollowedComedianSpecialsInRange(
  supabase: SupabaseClient<Database>,
  daysAgo: number,
  userID: string
): Promise<SpecialWithComedian[]> {
  // get all specials in l28d
  const allSpecials = await fetchSpecialsByTimeIntervalWithCache(
    daysAgo,
    supabase
  );

  // get all comedian IDs user follows
  const queryResponse = await supabase
    .from("user_comedian_follows")
    .select("comedian_id")
    .eq("user_id", userID);

  if (queryResponse.error) {
    console.error(queryResponse.error);
    throw new Error(
      `Error getting followed comedians for user ${userID}:
      ${queryResponse.error.message}`
    );
  }

  const followedComedians = queryResponse.data.map(
    (row) => row.comedian_id
  ) as string[];

  // filter specials for comedian IDs that user follows
  const specialsForFollowedComedians = allSpecials.filter((special) =>
    followedComedians.includes(special.comedian_id)
  );
  return specialsForFollowedComedians;
}

export async function fetchSpecialsByTimeIntervalWithCache(
  daysAgo: number,
  supabase: SupabaseClient
): Promise<SpecialWithComedian[]> {
  const cacheKey = generateCacheKey(
    CacheEntities.SPECIALS_LAST_28D_EMAIL,
    `days_${daysAgo}`
  );

  const cachedSpecials = await fetchWithCache(
    cacheKey,
    () => fetchSpecialsByTimeInterval(daysAgo, supabase),
    3600 // 1 hour TTL
  );

  return cachedSpecials;
}

export async function fetchSpecialsByTimeInterval(
  daysAgo: number,
  supabase: SupabaseClient
): Promise<SpecialWithComedian[]> {
  const targetDate = dayjs().subtract(daysAgo, "day").toISOString();
  const today = dayjs().toISOString();

  const { status, data, error } = await supabase
    .from("specials")
    .select(
      `*,
      comedian:comedians!specials_comedian_id_fkey!inner (
        slug,
        display_name,
        published_at
      )`
    )
    // special must be live
    .eq("is_live", true)
    // special must have been published within daysAgo and today
    .gte("published_at", targetDate)
    // comedian must have a published_at
    .not("comedian.published_at", "is", null)
    // comedian must have a published_at before today
    .lte("comedian.published_at", today)
    // comedian must not be an external preview
    .eq("comedian.is_external_preview", false)
    // order by published_at, ascending (latest first)
    .order("published_at", { ascending: false })
    .limit(10);

  if (error) {
    console.error(error);
    throw new Error(`Error getting trending specials: ${error.message}`);
  } else if (status === 200) {
    if (data) {
      return data.map((special) => {
        return {
          ...special,
          comedianSlug: special.comedian.slug,
          comedianDisplayName: special.comedian.display_name,
        };
      });
    } else {
      throw new Error("No data received");
    }
  } else {
    throw new Error(`Unexpected response status: ${status}`);
  }
}

export interface PartiallyPlayedSpecialData {
  updated_at: string;
  last_playback_location: number;
  special: SpecialRow;
  comedian: Comedian;
}

export async function getPartiallyPlayedSpecialsForUser(
  supabase: SupabaseClient<Database>
): Promise<PartiallyPlayedSpecialData[]> {
  const user = await getUser(supabase);
  if (!user) {
    return [];
  }

  return await getPartiallyPlayedSpecialsForUserId(supabase, user.id);
}

export async function getPartiallyPlayedSpecialsForUserId(
  supabase: SupabaseClient<Database>,
  userId: string,
  cursor: number = 0,
  limit: number = 20
): Promise<PartiallyPlayedSpecialData[]> {
  const { status, error, data } = await supabase
    .from("user_specials_playback")
    .select(
      `
      last_playback_location,
      updated_at,
      special:specials!user_specials_playback_special_id_fkey!inner (
        *,
        comedian:comedians!specials_comedian_id_fkey!inner (
          *
        )
      )
    `
    )
    .match({ user_id: userId })
    .order("updated_at", { ascending: false })
    .range(cursor, cursor + limit);

  if (error) {
    console.error(error);
  } else if (status === 200) {
    if (data) {
      const processedData = data.map((item) => {
        if (item.special && item.special.comedian) {
          const { comedian, ...special } = item.special;
          return {
            ...item,
            comedian,
            special,
          };
        }
      });
      const filteredData = processedData.filter((item) => item !== undefined);
      return filteredData as PartiallyPlayedSpecialData[];
    }
  }

  // return empty array as default case
  return [];
}

export async function getTrendingSpecials(
  supabase: SupabaseClient<Database>
): Promise<SpecialRow[]> {
  const targetDate = dayjs().subtract(14, "day").toISOString();
  const todayDate = dayjs().toISOString();

  // retrieve the trending specials
  const { data, error } = await supabase
    .from("specials")
    .select("*")
    .eq("is_live", true)
    .lte("published_at", todayDate)
    .gte("created_at", targetDate)
    .order("published_at", { ascending: false })
    .limit(10);

  if (error) {
    console.error(error);
  } else if (data) {
    return data;
  }

  throw new Error(`Issue getting trending specials`);
}

export type SpecialWithUnlocked = SpecialRow & { unlocked: boolean };
export type SpecialWithComedianAndUnlocked = SpecialRow & {
  unlocked: boolean;
};

export async function getSpecialsWatchedByUser({
  supabase,
  callerIsServer = false,
}: {
  supabase: SupabaseClient<Database>;
  callerIsServer?: boolean;
}): Promise<string[]> {
  let userId = "";
  if (callerIsServer) {
    const user = await getUser(supabase);
    if (!user) {
      throw new Error(
        "Supabase user must be logged in to get specials watched."
      );
    }
    userId = user.id;
  } else {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.user) {
      throw new Error("User must be logged in to get specials watched by user");
    }
    userId = session.user.id;
  }

  const { data: specialsWatchedByUser } = await supabase
    .from("user_specials_playback")
    .select("special_id")
    .match({ user_id: userId })
    .gte("last_playback_location", 30)
    .throwOnError();

  return specialsWatchedByUser?.map((special) => special.special_id) || [];
}

// Returns all published specials for fully published comedians (see full conditions below)
// Because Supabase limits to 1000 rows per query, this function will start causing issues in the future
// and require pagination
export async function getAllPublishedSpecials(): Promise<SpecialRow[] | null> {
  const supabase = createNoAuthSupabaseClient();
  const now = dayjs().toISOString();
  const { data: specials } = await supabase
    .from("specials")
    .select(
      `
      *,
      comedian:comedians!specials_comedian_id_fkey!inner (
        slug,
        display_name,
        published_at
      )
      `
    )
    // special must be live
    .eq("is_live", true)
    // special must have been published before now
    .lte("published_at", now)
    // comedian must not be an external preview
    .not("comedian.is_external_preview", "is", true)
    // comedian must have a published_at before now
    .lte("comedian.published_at", now)
    // comedian must have a published_at
    .not("comedian.published_at", "is", null)
    .throwOnError();

  return specials;
}

/**
 * Get vertical shorts (specials with runtime ≤ 3 minutes and vertical orientation)
 * Returns only published shorts from published comedians
 */
export async function getVerticalShortsWithComedian(
  limit: number = 10
): Promise<SpecialWithComedian[]> {
  const supabase = createNoAuthSupabaseClient();
  const now = dayjs().toISOString();

  const { status, data, error } = await supabase
    .from("specials")
    .select(
      `
      *,
      comedian:comedians!specials_comedian_id_fkey!inner (
        slug,
        display_name,
        published_at
      )
    `
    )
    .eq("is_live", true)
    .eq("is_vertical", true)
    .lte("runtime", 3)
    .lte("published_at", now)
    // comedian must have a published_at
    .not("comedian.published_at", "is", null)
    // comedian must not be an external preview
    .not("comedian.is_external_preview", "is", true)
    .order("published_at", { ascending: false })
    .limit(limit);

  if (error) {
    console.error(error);
  } else if (status === 200) {
    if (data) {
      return data.map((special) => {
        return {
          ...special,
          comedianSlug: special.comedian.slug,
          comedianDisplayName: special.comedian.display_name,
        };
      });
    }
  }
  throw new Error(`Issue getting vertical shorts with comedian info`);
}

export async function getTrendingSpecialsFromPrecomputed(
  supabase: SupabaseClient<Database>
): Promise<SpecialRow[]> {
  // Fetch the precomputed trending specials ordered by rank
  const { data: trendingData, error: trendingError } = await supabase
    .from("trending_specials")
    .select("special_id, rank")
    .order("rank", { ascending: true });

  if (trendingError || !trendingData || trendingData.length === 0) {
    console.error(
      "Error fetching precomputed trending specials:",
      trendingError
    );
    return await getTrendingSpecials(supabase);
  }

  const specialIds = trendingData.map((row) => row.special_id);

  const { data: specialsData, error: specialsError } = await supabase
    .from("specials")
    .select("*")
    .in("id", specialIds)
    .eq("is_live", true);

  if (specialsError || !specialsData || specialsData.length === 0) {
    console.error(
      "Error fetching special details for trending:",
      specialsError
    );
    return await getTrendingSpecials(supabase);
  }

  const specialsMap = Object.fromEntries(
    specialsData.map((special) => [special.id, special])
  );

  const sortedSpecials = specialIds
    .filter((id) => specialsMap[id])
    .map((id) => specialsMap[id]);

  return sortedSpecials;
}
